import { Group, Text, Textarea } from "@mantine/core";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Y from "yjs";

import type { LiveNutzer, WeeklyLive } from "../../hooks/useWeeklyLive.js";
import { caretKoordinaten } from "../../lib/weeklyLive/caret.js";
import { eingabeAlsAenderung, verschiebe, type Delta } from "../../lib/weeklyLive/textBinding.js";

/** Ursprung eigener Änderungen — die eigene Beobachtung überspringt sie. */
const EIGENE = Symbol("eigene-eingabe");

interface FremderCursor {
  clientId: number;
  user: LiveNutzer;
  index: number;
}

/** Cursor der anderen in diesem Feld, aus der Anwesenheit. */
function fremdeCursor(live: WeeklyLive, feld: string, ytext: Y.Text): FremderCursor[] {
  const out: FremderCursor[] = [];
  for (const [clientId, state] of live.anwesenheit.getStates()) {
    if (clientId === live.doc.clientID) continue;
    const c = state["cursor"] as { feld?: string; anker?: unknown } | null | undefined;
    const user = state["user"] as LiveNutzer | undefined;
    if (!c || c.feld !== feld || !user || !c.anker) continue;
    const pos = Y.createAbsolutePositionFromRelativePosition(Y.createRelativePositionFromJSON(c.anker), live.doc);
    if (pos && pos.type === ytext) out.push({ clientId, user, index: pos.index });
  }
  return out;
}

/**
 * Notizfeld im Live-Raum (#305): Der Text ist ein gemeinsamer Yjs-Text, jede
 * Eingabe geht sofort an alle, die Cursor der anderen stehen im Feld. Gespeichert
 * wird im Raum auf dem Server (gebündelt), nicht hier.
 */
export function LiveNoteField({
  live,
  feld,
  label,
  readOnly,
}: {
  live: WeeklyLive;
  feld: string;
  label?: string;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const ytext = live.doc.getText(feld);
  const [text, setText] = useState(() => ytext.toString());
  const [fremde, setFremde] = useState<FremderCursor[]>(() => fremdeCursor(live, feld, ytext));
  const feldRef = useRef<HTMLTextAreaElement>(null);
  const huelleRef = useRef<HTMLDivElement>(null);
  const auswahlNachher = useRef<[number, number] | null>(null);
  const [, neuZeichnen] = useState(0);

  // Andere tippen: Text übernehmen, die eigene Auswahl mitschieben.
  useEffect(() => {
    const beobachter = (e: Y.YTextEvent, tr: Y.Transaction) => {
      if (tr.origin === EIGENE) return;
      const el = feldRef.current;
      if (el && document.activeElement === el) {
        const delta = e.delta as Delta;
        auswahlNachher.current = [verschiebe(el.selectionStart, delta), verschiebe(el.selectionEnd, delta)];
      }
      setText(ytext.toString());
    };
    ytext.observe(beobachter);
    setText(ytext.toString());
    return () => ytext.unobserve(beobachter);
  }, [ytext]);

  useLayoutEffect(() => {
    const el = feldRef.current;
    if (el && auswahlNachher.current) {
      el.setSelectionRange(...auswahlNachher.current);
      auswahlNachher.current = null;
    }
  });

  useEffect(() => {
    const neu = () => setFremde(fremdeCursor(live, feld, ytext));
    live.anwesenheit.on("change", neu);
    live.doc.on("update", neu);
    return () => {
      live.anwesenheit.off("change", neu);
      live.doc.off("update", neu);
    };
  }, [live, feld, ytext]);

  // Bei Größenänderung des Felds die Cursor neu setzen.
  useEffect(() => {
    const el = feldRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => neuZeichnen((n) => n + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const meldeCursor = () => {
    const el = feldRef.current;
    if (!el || readOnly) return;
    live.anwesenheit.setLocalStateField("cursor", {
      feld,
      anker: Y.relativePositionToJSON(Y.createRelativePositionFromTypeIndex(ytext, el.selectionStart)),
    });
  };

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (readOnly) return;
    const neu = e.currentTarget.value;
    const a = eingabeAlsAenderung(ytext.toString(), neu, e.currentTarget.selectionEnd);
    if (a) {
      live.doc.transact(() => {
        if (a.loeschen) ytext.delete(a.index, a.loeschen);
        if (a.einfuegen) ytext.insert(a.index, a.einfuegen);
      }, EIGENE);
    }
    setText(neu);
    meldeCursor();
  };

  // Lage der fremden Cursor relativ zur Hülle.
  const el = feldRef.current;
  const huelle = huelleRef.current;
  const versatz =
    el && huelle
      ? { top: el.getBoundingClientRect().top - huelle.getBoundingClientRect().top, left: el.getBoundingClientRect().left - huelle.getBoundingClientRect().left }
      : null;

  return (
    <div ref={huelleRef} style={{ position: "relative" }}>
      <Textarea
        ref={feldRef}
        label={label}
        autosize
        minRows={2}
        value={text}
        readOnly={readOnly}
        placeholder={readOnly ? undefined : t("weekly.notesPlaceholder")}
        onChange={onChange}
        onSelect={meldeCursor}
        onFocus={meldeCursor}
        onBlur={() => live.anwesenheit.setLocalStateField("cursor", null)}
      />
      {el && versatz &&
        fremde.map((c) => {
          const k = caretKoordinaten(el, Math.min(c.index, text.length), text);
          return (
            <div
              key={c.clientId}
              aria-hidden
              style={{
                position: "absolute",
                top: versatz.top + k.top,
                left: versatz.left + k.left,
                height: k.hoehe,
                borderLeft: `2px solid ${c.user.farbe}`,
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  bottom: "100%",
                  left: -2,
                  background: c.user.farbe,
                  color: "white",
                  fontSize: 10,
                  lineHeight: "14px",
                  padding: "0 4px",
                  borderRadius: 3,
                  whiteSpace: "nowrap",
                }}
              >
                {c.user.name}
              </span>
            </div>
          );
        })}
      <Group gap="xs" mt={4} h={20}>
        {fremde.length > 0 && (
          <Text size="xs" c="dimmed">
            {t("weekly.live.typing", { names: [...new Set(fremde.map((c) => c.user.name))].join(", ") })}
          </Text>
        )}
      </Group>
    </div>
  );
}
